import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import {
  BoulderCompetitionData,
  BoulderClass,
  BoulderSportClass,
  Boulder,
  AthleteRankRow,
  formatScore,
  computeClassRankings,
} from '../utils/boulderCompetition';
import { subscribeBoulderData } from '../firebase/boulderService';
import { ATHLETE_BY_NAME } from '../data/athletes';
import ClassBadge from '../components/ClassBadge';

// ─── GroupResultTable ─────────────────────────────────────────────────────────

const GroupResultTable: React.FC<{
  cls: BoulderClass;
  rows: AthleteRankRow[];
  boulders: Boulder[];
  sportClasses: BoulderSportClass[];
}> = ({ cls, rows, boulders, sportClasses }) => {
  const grpBoulders = cls.boulderIds
    .map(bid => boulders.find(b => b.id === bid))
    .filter((b): b is Boulder => !!b);

  return (
    <section className="mb-8">
      <div className="bg-secondary text-white px-5 py-3 rounded-t-lg">
        <h2 className="text-xl font-bold tracking-wide">{cls.name}</h2>
        <p className="text-blue-200 text-sm mt-0.5">
          {rows.length} athlete{rows.length !== 1 ? 's' : ''} · {grpBoulders.length} boulder{grpBoulders.length !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="bg-white rounded-b-lg shadow overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100 text-gray-500 text-xs uppercase tracking-wide">
              <th className="px-4 py-2 text-center w-12">Rank</th>
              <th className="px-4 py-2 text-left min-w-36">Athlete</th>
              <th className="px-3 py-2 text-center w-16">Class</th>
              {grpBoulders.map(b => (
                <th key={b.id} className="px-3 py-2 text-center font-mono text-secondary w-16">
                  B{b.number}
                </th>
              ))}
              <th className="px-4 py-2 text-center w-20 font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const isFirst  = row.rank === 1 && row.total > 0;
              return (
                <tr
                  key={row.athlete.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 ${i === 0 && row.total > 0 ? 'bg-yellow-50' : ''}`}
                >
                  <td className="px-4 py-3 text-center font-bold text-lg text-secondary">
                    {row.total === 0 ? '—' : isFirst ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : row.rank}
                  </td>
                  <td className="px-4 py-3 font-semibold text-dark">
                    {ATHLETE_BY_NAME[row.athlete.name]?.id ? (
                      <Link to={`/athletes/${ATHLETE_BY_NAME[row.athlete.name].id}`} className="hover:text-primary hover:underline transition-colors">
                        {row.athlete.name}
                      </Link>
                    ) : row.athlete.name}
                    <span className="ml-1.5 text-gray-400 text-xs font-normal">
                      {row.athlete.gender === 'male' ? '♂' : '♀'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center text-xs">
                    <ClassBadge classId={sportClasses.find(sc => sc.id === row.athlete.classId)?.name || (row.athlete as any).paraclimbingClass || row.athlete.classId.toUpperCase()} />
                  </td>
                  {grpBoulders.map(b => {
                    const score = row.scores[b.id] ?? 0;
                    const isTop  = score > 6;
                    const isZone = score > 0 && score <= 6;
                    return (
                      <td
                        key={b.id}
                        className={`px-3 py-3 text-center font-mono font-bold ${
                          isTop  ? 'text-green-700 bg-green-50'
                        : isZone ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-300'
                        }`}
                      >
                        {score > 0 ? formatScore(score) : '—'}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-center font-extrabold text-primary text-base">
                    {row.total > 0 ? row.total.toFixed(1) : '—'}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={grpBoulders.length + 4} className="px-4 py-6 text-center text-gray-400 italic text-sm">
                  No athletes in this group.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// ─── BoulderResultsPage ───────────────────────────────────────────────────────

const BoulderResultsPage: React.FC = () => {
  const navigate     = useNavigate();
  const { competitionId = 'bk2026' } = useParams<{ competitionId: string }>();
  const [data,       setData]       = useState<BoulderCompetitionData | null>(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [connError,  setConnError]  = useState(false);

  useEffect(() => {
    const unsub = subscribeBoulderData(
      competitionId,
      d => {
        setData(d);
        setLastUpdate(new Date());
        setConnError(false);
      },
      () => setConnError(true),
    );
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId]);

  if (!data) {
    if (connError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-dark text-white text-center px-4">
          <div>
            <div className="text-4xl mb-4">⚠</div>
            <p className="text-xl font-semibold">Could not connect to Firestore</p>
            <p className="text-gray-400 mt-2 text-sm">Check your internet connection or Firestore security rules.</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-secondary rounded text-sm">
              Retry
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark text-white text-2xl">
        Loading…
      </div>
    );
  }

  const rankings = computeClassRankings(data);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero header */}
      <div className="bg-gradient-to-r from-dark to-secondary text-white py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(`/competition/${competitionId}`)} className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm mb-5 transition-colors">
            ← Competition hub
          </button>
        </div>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-end justify-between gap-2">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              Boulder Competition
            </h1>
            <p className="text-blue-200 mt-1 text-lg">Live Results</p>
          </div>
          <div className="text-right text-xs text-blue-300">
            {connError ? (
              <span className="text-red-300">⚠ Connection error</span>
            ) : (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse mr-1" />
                Live · updated {lastUpdate.toLocaleTimeString()}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Scoring legend */}
        <div className="mb-6 flex flex-wrap gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded bg-green-50 border border-green-200" />
            Top (green) = 10 pts − 0.1 per attempt
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded bg-blue-50 border border-blue-200" />
            Zone (blue) = 4 pts − 0.1 per attempt
          </span>
        </div>

        {rankings.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
            <p className="text-lg">Results will appear here once the competition starts.</p>
          </div>
        ) : (
          rankings.map(({ cls, rows }) => (
            <GroupResultTable
              key={cls.id}
              cls={cls}
              rows={rows}
              boulders={data.setup.boulders}
              sportClasses={data.setup.sportClasses}
            />
          ))
        )}

      </div>
    </div>
  );
};

export default BoulderResultsPage;
